import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useEffect, useMemo, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { AppBrandMark } from '@/components/brand/AppBrandMark';
import { cn } from '@/lib/utils';
import { resolveAssetUrl, resolveTenantLogo } from '@/lib/tenantBranding';

type SidebarLink = {
  name: string;
  path: string;
  icon?: React.ReactNode;
  kind?: 'link';
};

type SidebarHeader = {
  name: string;
  icon?: React.ReactNode;
  kind: 'header';
};

type SidebarGroup = {
  name: string;
  icon?: React.ReactNode;
  kind: 'group';
  children: SidebarLink[];
};

type SidebarSection = SidebarLink | SidebarHeader | SidebarGroup;

interface SidebarProps {
  sections: SidebarSection[];
}

export function Sidebar({ sections }: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user, branding } = useAuth();
  const teamLogo = user?.teamBranding?.logoUrl;

  const isActive = (path: string) => {
    const pathname = location.pathname;
    if (pathname === path) return true;
    // Command Centre is the sole Intelligence sidebar entry — stay active on domain pages.
    if (
      (path === '/hospital-admin/intelligence' || path === '/super-admin/intelligence') &&
      pathname.startsWith(`${path}/`)
    ) {
      return true;
    }
    return false;
  };

  const initialOpenGroups = useMemo(() => {
    const open: Record<string, boolean> = {};
    for (const s of sections) {
      if (s.kind === 'group') {
        open[s.name] = s.children.some((c) => location.pathname.startsWith(c.path));
      }
    }
    return open;
  }, [sections, location.pathname]);

  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(initialOpenGroups);

  useEffect(() => {
    setOpenGroups((prev) => {
      const next = { ...prev };
      for (const s of sections) {
        if (s.kind === 'group' && s.children.some((c) => location.pathname.startsWith(c.path))) {
          next[s.name] = true;
        }
      }
      return next;
    });
  }, [location.pathname, sections]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navLinkClass = (active: boolean) =>
    cn(
      'relative flex items-center space-x-3 rounded-r-xl py-2.5 pl-3 pr-5 text-sm font-medium transition-colors',
      active
        ? 'bg-artis-sand/80 text-artis-brown-dark'
        : 'text-artis-brown hover:bg-artis-sand/50'
    );

  return (
    <aside className="flex h-full w-full flex-col pointer-events-auto">
      <div className="flex h-20 flex-shrink-0 items-center gap-2 border-b border-artis-brown/10 px-3">
        {teamLogo ? (
          <>
            <img
              src={resolveTenantLogo(branding ?? user?.branding)}
              alt="Hospital"
              className="max-h-[44px] max-w-[100px] object-contain"
            />
            <img
              src={resolveAssetUrl(teamLogo)}
              alt={user?.teamBranding?.teamLeadName ?? 'Doctor team'}
              className="max-h-[44px] max-w-[100px] object-contain border-l border-artis-brown/15 pl-2"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          </>
        ) : (
          <AppBrandMark variant="light" compact className="max-h-[60px] max-w-[222px]" />
        )}
      </div>

      <nav className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto px-1 py-2">
        {sections.map((section, idx) => {
          if ('kind' in section && section.kind === 'header') {
            const isFirst = idx === 0;
            return (
              <div
                key={`header-${idx}-${section.name}`}
                className={cn(
                  'mx-1 mb-0.5 rounded-md px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wider text-artis-slate',
                  isFirst ? 'mt-0' : 'mt-2'
                )}
              >
                {section.name}
              </div>
            );
          }

          if ('kind' in section && section.kind === 'group') {
            const g = section;
            const isOpen = openGroups[g.name] ?? false;
            const groupHasActive = g.children.some((c) => isActive(c.path));
            return (
              <div key={`group-${idx}-${g.name}`} className="mb-0.5">
                <button
                  type="button"
                  onClick={() => setOpenGroups((p) => ({ ...p, [g.name]: !isOpen }))}
                  className={cn(
                    'flex w-full items-center justify-between rounded-lg py-2 pl-2.5 pr-2 text-left text-[13px] font-semibold transition-colors',
                    groupHasActive
                      ? 'bg-artis-sand/60 text-artis-brown-dark'
                      : 'text-artis-brown hover:bg-artis-sand/40',
                  )}
                >
                  <span className="flex min-w-0 items-center gap-2">
                    {g.icon ? <span>{g.icon}</span> : null}
                    <span className="leading-tight">{g.name}</span>
                  </span>
                  <ChevronDown
                    className={cn('h-3.5 w-3.5 shrink-0 opacity-70 transition-transform', isOpen && 'rotate-180')}
                  />
                </button>
                {isOpen && (
                  <div className="mb-1 ml-1 flex flex-col border-l border-artis-brown/10 pl-1">
                    {g.children.map((c) => (
                      <Link key={c.path} to={c.path} className={navLinkClass(isActive(c.path))}>
                        {isActive(c.path) && (
                          <div className="absolute left-0 top-0 h-full w-1 rounded-r-full bg-artis-gold" />
                        )}
                        <span className="pl-1.5 text-[13px] leading-snug">{c.name}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          }

          const link = section as SidebarLink;
          return (
            <Link key={link.path || `link-${idx}-${link.name}`} to={link.path} className={navLinkClass(isActive(link.path))}>
              {isActive(link.path) && (
                <div className="absolute left-0 top-0 h-full w-1 rounded-r-full bg-artis-gold" />
              )}
              {link.icon && <span>{link.icon}</span>}
              <span>{link.name}</span>
            </Link>
          );
        })}
      </nav>

      <div className="flex-shrink-0 border-t border-artis-brown/10 bg-artis-cream py-3">
        <button
          type="button"
          onClick={handleLogout}
          className="flex w-full items-center rounded-r-xl py-2.5 pl-3 pr-5 text-sm font-semibold text-red-700 transition-colors hover:bg-red-50"
        >
          Logout
        </button>
      </div>
    </aside>
  );
}
